import { HoneycombWebSDK } from "@honeycombio/opentelemetry-web";
import type { Sampler, SpanProcessor } from "@opentelemetry/sdk-trace-web";

import { markTelemetryStarted, setTelemetryFlushHandler } from "@/services/telemetry";

/**
 * The only file allowed to import Honeycomb packages. All instrumentation goes through
 * `@opentelemetry/api` (see `@/services/telemetry`), so re-pointing the exporter at another
 * backend later is a change to this file alone.
 */
export type HoneycombConfig = {
  ingestApiKey: string;
  serviceName: string;
  active: boolean;
  /** Head sampler (see `SessionCategorySampler`); omitted means sample everything. */
  sampler?: Sampler;
  /** Extra processors, e.g. the session-attribute stamper. */
  spanProcessors?: SpanProcessor[];
  /** Static resource attributes (build, surface) attached to every span. */
  resourceAttributes?: Record<string, string | number | boolean>;
};

let honeycombSdk: HoneycombWebSDK | undefined;

export function initializeHoneycomb(config: HoneycombConfig) {
  if (!config.active) {
    return;
  }
  honeycombSdk = new HoneycombWebSDK({
    apiKey: config.ingestApiKey,
    serviceName: config.serviceName,
    instrumentations: [],
    // The SDK's built-in web-vitals and global-errors instrumentations default ON even with an
    // empty instrumentation list. Both duplicate signals owned elsewhere: vitals ship as
    // `vitals`-category `web_vitals.*` events (init/webVitals.ts), and unrecoverable errors are
    // Sentry's job.
    webVitalsInstrumentationConfig: { enabled: false },
    globalErrorsInstrumentationConfig: { enabled: false },
    sampler: config.sampler,
    spanProcessors: config.spanProcessors,
    resourceAttributes: config.resourceAttributes,
  });
  honeycombSdk.start();
  setTelemetryFlushHandler(async () => {
    await honeycombSdk?.forceFlush();
  });
  markTelemetryStarted();
}
